/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { DataTable, SearchSelect, PillToggle } from "../../components/shared";
import { VendorStats } from "../../components/admin";
import { useToastContext } from "../../contexts/ToastContext";
import {
 useVendors,
 useVerificationStats,
 useBulkVerifyVendors,
 useDeletePendingVendor,
 useSuspendVendor,
 vendorKeys,
} from "../../services/vendorService";
import { useSubscriptions } from "../../services/subscriptionService";
import {
 Eye,
 CheckCircle,
 XCircle,
 AlertCircle,
 MapPin,
 Star,
 Calendar,
 Users,
 Download,
 Wifi,
 WifiOff,
 RefreshCw,
 Shield,
 Trash2,
 ListChecks,
 Loader2,
} from "lucide-react";
import { AdminPageHeader, AdminGuideCallout } from "../../components/admin/guidance";
import { ADMIN_PAGE_GUIDES } from "../../utils/adminPageGuides";
import StripeConnectStatusBadge from "../../components/shared/StripeConnectStatusBadge";

const AdminVendors = () => {
 const navigate = useNavigate();
 const { success, error: showError, info } = useToastContext();
 const queryClient = useQueryClient();
 const [selectedVendors, setSelectedVendors] = useState([]);
 const [activeSubscriptionTab, setActiveSubscriptionTab] = useState("all");
 const [togglingVendorId, setTogglingVendorId] = useState(null);
 const [filters, setFilters] = useState({
 verification: "all",
 featured: "all",
 onlineStatus: "all",
 subscriptionStatus: "all",
 });

 // Fetch subscription plans for tabs
 const { data: subscriptionsData } = useSubscriptions({ per_page: 50 });
 const subscriptionPlans = subscriptionsData?.data || [];

 // Helper function to check if user is online (now uses server-calculated status)
 const isUserOnline = (vendor) => {
 // Use the server-calculated online status if available
 if (vendor.is_online !== undefined) {
 return vendor.is_online;
 }
 
 // Fallback to last login check for backward compatibility
 if (!vendor.last_logged_in) return false;
 
 const lastLoginTime = new Date(vendor.last_logged_in);
 const now = new Date();
 const timeDiff = now - lastLoginTime;
 
 // Consider user online if they logged in within the last 5 minutes
 const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
 return timeDiff < fiveMinutes;
 };

 // Helper function to get time since last activity
 const getTimeSinceLastActivity = (vendor) => {
 // Use last_activity if available, otherwise fallback to last_logged_in
 const activityTime = vendor.last_activity || vendor.last_logged_in;
 
 if (!activityTime) return "Never";
 
 const activityDate = new Date(activityTime);
 const now = new Date();
 const timeDiff = now - activityDate;
 
 const minutes = Math.floor(timeDiff / (1000 * 60));
 const hours = Math.floor(timeDiff / (1000 * 60 * 60));
 const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
 
 if (minutes < 1) return "Just now";
 if (minutes < 60) return `${minutes}m ago`;
 if (hours < 24) return `${hours}h ago`;
 return `${days}d ago`;
 };

 // React Query hooks
 const {
 data: vendorsData,
 isLoading: vendorsLoading,
 error: vendorsError,
 refetch: refetchVendors,
 } = useVendors({
 per_page: 50,
 verified:
 filters.verification === "verified"
 ? true
 : filters.verification === "pending"
 ? false
 : undefined,
 featured:
 filters.featured === "yes"
 ? true
 : filters.featured === "no"
 ? false
 : undefined,
 });

 const { 
 data: statsData, 
 isLoading: statsLoading, 
 error: statsError 
 } = useVerificationStats();

 // Listen for logout events to refresh vendor data
 useEffect(() => {
 const handleLogout = () => {
 // Invalidate and refetch vendor data when a user logs out
 console.log('User logged out, refreshing vendor data...');
 queryClient.invalidateQueries({ queryKey: vendorKeys.all });
 };

 // Listen for logout events from AuthContext
 window.addEventListener('userLoggedOut', handleLogout);

 return () => {
 window.removeEventListener('userLoggedOut', handleLogout);
 };
 }, [queryClient]);

 // Mutations
 const bulkVerifyMutation = useBulkVerifyVendors();
 const deletePendingMutation = useDeletePendingVendor();
 const suspendVendorMutation = useSuspendVendor();

 const isVendorActive = (vendor) =>
 vendor.is_active !== false && vendor.vendor_profile?.is_active !== false;

 const handleToggleVendorActive = (vendor) => {
 const currentlyActive = isVendorActive(vendor);
 const nextActive = !currentlyActive;
 const vendorName = vendor.vendor_profile?.business_name || vendor.name || "this vendor";

 if (!nextActive) {
 const confirmed = window.confirm(
 `Suspend "${vendorName}"? They will be logged out immediately and notified by email.`
 );
 if (!confirmed) return;
 }

 setTogglingVendorId(vendor.id);
 suspendVendorMutation.mutate(
 {
 id: vendor.id,
 isActive: nextActive,
 reason: null,
 },
 {
 onSuccess: (_, variables) => {
 success(
 variables.isActive ? "Vendor Reactivated" : "Vendor Suspended",
 variables.isActive
 ? `${vendorName} can log in and access their account again.`
 : `${vendorName} has been suspended and logged out.`
 );
 setTogglingVendorId(null);
 },
 onError: (err) => {
 showError(
 "Update Failed",
 err?.response?.data?.message || err?.message || "Failed to update vendor status"
 );
 setTogglingVendorId(null);
 },
 }
 );
 };

 // Extract and filter data
 const allVendors = vendorsData?.data?.data || [];

 const getVendorSubscriptions = (vendor) =>
 vendor.vendor_subscriptions || vendor.vendorSubscriptions || [];

 const isVendorSubscriptionExpired = (vendor) => {
 const subs = getVendorSubscriptions(vendor);
 const activeSubscription = subs.find((s) => s.status === "active");
 const expiredSubscription = subs.find((s) => s.status === "expired");
 const subscriptionExpiresAt = vendor.subscription_expires_at;

 if (subscriptionExpiresAt) {
 return new Date(subscriptionExpiresAt).getTime() <= Date.now();
 }

 return Boolean(expiredSubscription && !activeSubscription);
 };

 // Helper to get vendor's active subscription plan name
 const getVendorPlanName = (vendor) => {
 const subs = getVendorSubscriptions(vendor);
 const activeSub = subs.find((s) => s.status === "active");
 return activeSub?.subscription?.name || null;
 };

 const getVendorSubscriptionDisplay = (vendor) => {
 const subs = getVendorSubscriptions(vendor);
 const activeSubscription = subs.find((s) => s.status === "active");
 const expiredSubscription = subs.find((s) => s.status === "expired");
 const subscriptionExpiresAt = vendor.subscription_expires_at;

 if (!activeSubscription && !expiredSubscription && !subscriptionExpiresAt) {
 return { hasSubscription: false };
 }

 let daysRemaining = 0;
 let isExpired = false;

 if (subscriptionExpiresAt) {
 const timeLeft = new Date(subscriptionExpiresAt).getTime() - Date.now();
 daysRemaining = Math.max(0, Math.floor(timeLeft / (1000 * 60 * 60 * 24)));
 isExpired = timeLeft <= 0;
 } else if (expiredSubscription && !activeSubscription) {
 isExpired = true;
 }

 const planName =
 activeSubscription?.subscription?.name
 || expiredSubscription?.subscription?.name
 || subs[0]?.subscription?.name
 || "Unknown Plan";

 return { hasSubscription: true, planName, daysRemaining, isExpired };
 };

 const expiredVendorCount = allVendors.filter(isVendorSubscriptionExpired).length;

 const getVendorCountryCode = (vendor) => {
 const code = vendor.country_code || vendor.countryCode;
 if (code) return String(code).toUpperCase();
 return null;
 };

 const getVendorCountryName = (vendor) =>
 vendor.country || vendor.vendor_profile?.country || null;

 // Apply subscription tab filter
 const vendorsBySubscription = allVendors.filter((vendor) => {
 if (activeSubscriptionTab === "expired") {
 return isVendorSubscriptionExpired(vendor);
 }

 const planName = getVendorPlanName(vendor);
 if (activeSubscriptionTab === "all") return true;
 if (activeSubscriptionTab === "none") return !planName;
 return planName === activeSubscriptionTab;
 });

 // Apply online status + subscription status filters
 const vendors = vendorsBySubscription.filter((vendor) => {
 if (filters.subscriptionStatus === "expired" && !isVendorSubscriptionExpired(vendor)) {
 return false;
 }
 if (filters.subscriptionStatus === "active" && isVendorSubscriptionExpired(vendor)) {
 return false;
 }

 if (filters.onlineStatus === "all") return true;
 
 const isOnline = isUserOnline(vendor);
 if (filters.onlineStatus === "online") return isOnline;
 if (filters.onlineStatus === "offline") return !isOnline;
 
 return true;
 });
 
 const loading = vendorsLoading || statsLoading;
 const error = vendorsError?.message || null;
 // console.log("====================================");
 // console.log("Stats Data:", statsData);
 // console.log("Stats Error:", statsError);
 // console.log("Vendors Data:", vendors);
 // console.log("====================================");
 const stats = {
 total: statsData?.data?.total_vendors || 0,
 verified: statsData?.data?.total_verified || 0,
 pending: statsData?.data?.total_pending || 0,
 rejected: 0, // This would need to be added to the API
 };

 // Table columns configuration
 const columns = [
 {
 key: "user_name",
 label: " Name",
 sortable: true,
 searchable: true,
 width: "w-1/4",
 render: (vendor) => (
 <div className="flex items-center min-w-0">
 <div className="flex-shrink-0 h-8 w-8">
 <div className="h-8 w-8 rounded-full bg-surface-muted flex items-center justify-center">
 <span className="text-xs font-medium text-ink">
 {vendor.vendor_profile?.business_name
 ?.charAt(0)
 ?.toUpperCase() ||
 vendor.name?.charAt(0)?.toUpperCase() ||
 "V"}
 </span>
 </div>
 </div>
 <div className="ml-3 min-w-0 flex-1">
 <div className="text-sm font-medium text-gray-900 truncate">
 {vendor.vendor_profile?.business_name || vendor.name || "N/A"}
 </div>
 <div className="text-xs text-gray-500 truncate mt-0.5">
 {vendor.email || "N/A"}
 </div>
 </div>
 </div>
 ),
 },
 // {
 // key: "business_name",
 // label: "Business Details",
 // sortable: true,
 // searchable: true,
 // width: "w-1/4",
 // render: (vendor) => (
 // <div className="flex items-center min-w-0">
 // <div className="flex-shrink-0 h-8 w-8">
 // <div className="h-8 w-8 rounded-full bg-surface-muted flex items-center justify-center">
 // <span className="text-xs font-medium text-ink">
 // {vendor.vendor_profile?.business_name
 // ?.charAt(0)
 // ?.toUpperCase() || "B"}
 // </span>
 // </div>
 // </div>
 // <div className="ml-3 min-w-0 flex-1">
 // <div className="text-sm font-medium text-gray-900 truncate">
 // {vendor.vendor_profile?.business_name 
 // ? vendor.vendor_profile.business_name.length > 20 
 // ? `${vendor.vendor_profile.business_name.substring(0, 20)}...` 
 // : vendor.vendor_profile.business_name
 // : "No Business Name"}
 // </div>
 // <div className="text-xs text-gray-500 truncate">
 // {vendor.vendor_profile?.description 
 // ? vendor.vendor_profile.description.length > 30 
 // ? `${vendor.vendor_profile.description.substring(0, 30)}...` 
 // : vendor.vendor_profile.description
 // : "No description available"}
 // </div>
 // </div>
 // </div>
 // ),
 // },
 // {
 // key: "location",
 // label: "Location",
 // sortable: true,
 // width: "w-1/6",
 // render: (vendor) => (
 // <div className="flex items-center text-sm text-gray-900 min-w-0">
 // <MapPin className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
 // <span className="truncate">
 // {vendor.vendor_profile?.city && vendor.vendor_profile?.state
 // ? `${vendor.vendor_profile.city}, ${vendor.vendor_profile.state}`
 // : "Not set"}
 // </span>
 // </div>
 // ),
 // },
 // {
 // key: 'average_rating',
 // label: 'Rating',
 // sortable: true,
 // render: (vendor) => (
 // <div className="flex items-center">
 // <Star className="w-4 h-4 text-yellow-400 mr-1" />
 // <span className="text-sm font-medium">
 // {vendor.vendor_profile?.average_rating ? vendor.vendor_profile.average_rating : 'N/A'}
 // </span>
 // <span className="text-sm text-gray-500 ml-1">
 // ({vendor.vendor_profile?.total_reviews || 0} reviews)
 // </span>
 // </div>
 // )
 // },
 {
 key: "country_code",
 label: "Country",
 sortable: true,
 width: "w-20",
 render: (vendor) => {
 const countryCode = getVendorCountryCode(vendor);
 const countryName = getVendorCountryName(vendor);
 const city = vendor.city || vendor.vendor_profile?.city;

 return (
 <div
 className="min-w-0"
 title={[countryName, city].filter(Boolean).join(" · ") || "Country not set"}
 >
 <span
 className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${ countryCode ? "bg-slate-100 text-slate-800" : "bg-gray-100 text-gray-400" }`}
 >
 {countryCode || "—"}
 </span>
 {(city || countryName) && (
 <div className="text-xs text-gray-500 mt-1 truncate max-w-[6rem]">
 {city || countryName}
 </div>
 )}
 </div>
 );
 },
 },
 {
 key: "account_status",
 label: "Account",
 sortable: true,
 width: "w-28",
 render: (vendor) => (
 <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-start gap-1">
 <PillToggle
 checked={isVendorActive(vendor)}
 onChange={() => handleToggleVendorActive(vendor)}
 loading={togglingVendorId === vendor.id && suspendVendorMutation.isPending}
 disabled={togglingVendorId === vendor.id && suspendVendorMutation.isPending}
 variant={isVendorActive(vendor) ? "success" : "amber"}
 onLabel="Active"
 offLabel="Susp"
 ariaLabel={`Toggle account status for ${vendor.vendor_profile?.business_name || vendor.name}`}
 />
 {!isVendorActive(vendor) && (
 <span className="text-xs text-red-600 font-medium">Suspended</span>
 )}
 </div>
 ),
 },
 {
 key: "status_featured",
 label: "Status / Featured",
 sortable: true,
 width: "w-1/6",
 render: (vendor) => (
 <div className="flex flex-col gap-1">
 <div>
 {vendor.vendor_profile?.is_verified ? (
 <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800">
 <CheckCircle className="mr-1 h-2.5 w-2.5" />
 Verified
 </span>
 ) : (
 <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-medium text-yellow-800">
 <AlertCircle className="mr-1 h-2.5 w-2.5" />
 Pending
 </span>
 )}
 </div>
 <div>
 <StripeConnectStatusBadge
 connect={vendor.vendor_profile?.stripe_connect}
 showPayoutReady={false}
 />
 </div>
 <div>
 <span
 className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${ vendor.vendor_profile?.is_featured ? "bg-surface-muted text-ink" : "bg-gray-100 text-gray-800" }`}
 >
 {vendor.vendor_profile?.is_featured ? "Featured" : "—"}
 </span>
 </div>
 </div>
 ),
 },
 {
 key: "created_at",
 label: "Joined",
 sortable: true,
 type: "date",
 width: "w-1/6",
 render: (vendor) => (
 <div className="flex items-center text-sm text-gray-900 min-w-0">
 <Calendar className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
 <span className="truncate">
 {new Date(vendor.created_at).toLocaleDateString('en-US', {
 year: 'numeric',
 month: 'short',
 day: 'numeric'
 })}
 </span>
 </div>
 ),
 },
 {
 key: "online_status",
 label: "Status",
 sortable: true,
 width: "w-1/6",
 render: (vendor) => {
 const isOnline = isUserOnline(vendor);
 const timeAgo = getTimeSinceLastActivity(vendor);
 
 return (
 <div 
 className="flex items-center text-sm min-w-0 cursor-help" 
 title={`Status: ${isOnline ? 'Online' : 'Offline'} | Last activity: ${vendor.last_activity ? new Date(vendor.last_activity).toLocaleString() : vendor.last_logged_in ? new Date(vendor.last_logged_in).toLocaleString() : 'Never'}${getVendorCountryName(vendor) ? ` | Location: ${getVendorCountryName(vendor)}` : ''}${getVendorCountryCode(vendor) ? ` (${getVendorCountryCode(vendor)})` : ''}${vendor.ip_address ? ` | IP: ${vendor.ip_address}` : ''}`}
 >
 <div className="relative flex items-center">
 {/* Ping animation dot */}
 <div className="relative">
 {isOnline ? (
 <div className="relative">
 {/* Ping ring animation */}
 <div className="absolute -top-1 -left-1 w-3 h-3 bg-green-400 rounded-full animate-ping opacity-75"></div>
 {/* Second ping ring for better effect */}
 <div className="absolute -top-1 -left-1 w-3 h-3 bg-green-300 rounded-full animate-ping opacity-50" style={{ animationDelay: '0.5s' }}></div>
 {/* Main dot */}
 <div className="relative w-2 h-2 bg-green-500 rounded-full shadow-sm"></div>
 </div>
 ) : (
 <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
 )}
 </div>
 
 {/* Status text and icon */}
 <div className="ml-2 flex items-center">
 {isOnline ? (
 <Wifi className="w-3 h-3 text-green-500 mr-1" />
 ) : (
 <WifiOff className="w-3 h-3 text-gray-400 mr-1" />
 )}
 <div className="flex flex-col">
 <span className={`text-xs font-medium ${ isOnline ? 'text-green-700' : 'text-gray-600' }`}>
 {isOnline ? 'Online' : 'Offline'}
 </span>
 <span className="text-xs text-gray-500">
 {timeAgo}
 </span>
 {getVendorCountryCode(vendor) && (
 <span className="text-xs font-medium text-gray-500">
 {getVendorCountryCode(vendor)}
 </span>
 )}
 </div>
 </div>
 </div>
 </div>
 );
 },
 },
 {
 key: "subscription",
 label: "Subscription",
 sortable: false,
 width: "w-1/6",
 render: (vendor) => {
 const subscription = getVendorSubscriptionDisplay(vendor);

 if (!subscription.hasSubscription) {
 return (
 <div className="flex items-center text-sm text-gray-500">
 <Shield className="w-3 h-3 mr-1 text-gray-400" />
 No subscription
 </div>
 );
 }

 const { planName, daysRemaining, isExpired } = subscription;
 const statusClass = isExpired
 ? "text-red-600 font-semibold"
 : daysRemaining <= 7
 ? "text-red-600 font-medium"
 : daysRemaining <= 30
 ? "text-yellow-600"
 : "text-gray-600";

 return (
 <div className="text-sm">
 <div className="flex items-center text-gray-900 mb-1">
 <Shield className="w-3 h-3 mr-1 text-gray-400" />
 <span className={`font-medium ${isExpired ? "text-gray-500" : ""}`}>
 {planName}
 </span>
 </div>
 <div className="flex items-center text-gray-600">
 <Calendar className="w-3 h-3 mr-1 text-gray-400" />
 <span className={statusClass}>
 {isExpired ? "Expired" : `${daysRemaining} days left`}
 </span>
 </div>
 </div>
 );
 },
 },
 ];

 const handleDeletePendingVendor = (vendor) => {
 if (!window.confirm(`Are you sure you want to delete "${vendor.vendor_profile?.business_name || vendor.name}"? This action cannot be undone.`)) {
 return;
 }
 deletePendingMutation.mutate(vendor.id, {
 onSuccess: () => {
 success("Vendor Deleted", "Pending vendor has been deleted successfully");
 },
 onError: (err) => {
 showError("Delete Failed", err?.response?.data?.message || err?.message || "Failed to delete vendor");
 }
 });
 };

 // Table actions - View for all, Delete only for pending vendors
 const actions = [
 {
 label: "View",
 icon: Eye,
 variant: "default",
 onClick: (vendor) => {
 navigate(`/admin/vendors/${vendor.id}`);
 },
 },
 {
 label: "Delete",
 icon: Trash2,
 variant: "danger",
 title: "Delete pending vendor",
 show: (vendor) => !vendor.vendor_profile?.is_verified,
 onClick: handleDeletePendingVendor,
 },
 ];

 // Handle bulk actions
 const handleBulkVerify = () => {
 if (selectedVendors.length === 0) {
 showError("No Selection", "Please select vendors to verify");
 return;
 }

 bulkVerifyMutation.mutate({
 vendorIds: selectedVendors.map((v) => v.vendor_profile?.id || v.id),
 data: { verification_level: "verified" },
 }, {
 onSuccess: () => {
 success("Bulk Verification", `Successfully verified ${selectedVendors.length} vendors`);
 setSelectedVendors([]);
 },
 onError: (error) => {
 showError("Verification Failed", error.message || "Failed to verify vendors");
 }
 });
 };


 // Handle search
 const handleSearch = (data, searchTerm) => {
 return data.filter((vendor) => {
 const businessName = vendor.vendor_profile?.business_name || "";
 const businessDescription = vendor.vendor_profile?.description || "";
 const name = vendor.name || "";
 const email = vendor.email || "";
 const city = vendor.vendor_profile?.city || vendor.city || "";
 const state = vendor.vendor_profile?.state || vendor.state || "";
 const countryCode = getVendorCountryCode(vendor) || "";
 const onlineStatus = isUserOnline(vendor) ? "online" : "offline";
 const timeAgo = getTimeSinceLastActivity(vendor);

 return (
 businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
 businessDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
 name.toLowerCase().includes(searchTerm.toLowerCase()) ||
 email.toLowerCase().includes(searchTerm.toLowerCase()) ||
 city.toLowerCase().includes(searchTerm.toLowerCase()) ||
 state.toLowerCase().includes(searchTerm.toLowerCase()) ||
 countryCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
 onlineStatus.includes(searchTerm.toLowerCase()) ||
 timeAgo.toLowerCase().includes(searchTerm.toLowerCase())
 );
 });
 };

 // Handle row click
 const handleRowClick = (vendor) => {
 navigate(`/admin/vendors/${vendor.id}`);
 };

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Vendor Management"
 subtitle="Manage and verify vendor accounts"
 actions={
 <>
 {selectedVendors.length > 0 && (
 <button
 type="button"
 onClick={handleBulkVerify}
 className="inline-flex items-center justify-center rounded-product-md border border-transparent bg-ink px-4 py-1.5 text-xs font-medium text-white hover:bg-neutral-800"
 >
 <CheckCircle className="w-4 h-4 mr-2" />
 <span className="hidden sm:inline">Verify Selected ({selectedVendors.length})</span>
 <span className="sm:hidden">Verify ({selectedVendors.length})</span>
 </button>
 )}
 <button
 type="button"
 className="inline-flex items-center justify-center rounded-product-md border border-border bg-surface px-4 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
 >
 <Download className="w-4 h-4 mr-2" />
 Export
 </button>
 </>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.vendors.title}
 steps={ADMIN_PAGE_GUIDES.vendors.steps}
 icon={ListChecks}
 />

 <VendorStats
 totalVendors={stats.total}
 verifiedVendors={stats.verified}
 pendingVendors={stats.pending}
 rejectedVendors={stats.rejected}
 />

 <div className="product-panel">
 <div className="product-panel-header">
 <div
 className="product-tabs"
 role="tablist"
 aria-label="Subscription plan"
 >
 <button
 type="button"
 role="tab"
 aria-selected={activeSubscriptionTab === "all"}
 onClick={() => setActiveSubscriptionTab("all")}
 className={`product-tab ${ activeSubscriptionTab === "all" ? "is-active" : "" }`}
 >
 All <span className="tabular-nums opacity-70">({allVendors.length})</span>
 </button>
 {subscriptionPlans
 .filter((p) => p.is_active !== false)
 .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
 .map((plan) => {
 const count = allVendors.filter(
 (v) => getVendorPlanName(v) === plan.name
 ).length;
 const isActive = activeSubscriptionTab === plan.name;
 return (
 <button
 key={plan.id}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setActiveSubscriptionTab(plan.name)}
 className={`product-tab inline-flex items-center gap-1.5 ${ isActive ? "is-active" : "" }`}
 >
 <Shield className="w-3.5 h-3.5" />
 {plan.name}{" "}
 <span className="tabular-nums opacity-70">({count})</span>
 </button>
 );
 })}
 <button
 type="button"
 role="tab"
 aria-selected={activeSubscriptionTab === "none"}
 onClick={() => setActiveSubscriptionTab("none")}
 className={`product-tab ${ activeSubscriptionTab === "none" ? "is-active" : "" }`}
 >
 No subscription{" "}
 <span className="tabular-nums opacity-70">
 ({allVendors.filter((v) => !getVendorPlanName(v)).length})
 </span>
 </button>
 <button
 type="button"
 role="tab"
 aria-selected={activeSubscriptionTab === "expired"}
 onClick={() => {
 setActiveSubscriptionTab("expired");
 setFilters((prev) => ({ ...prev, subscriptionStatus: "all" }));
 }}
 className={`product-tab inline-flex items-center gap-1.5 ${ activeSubscriptionTab === "expired" ? "is-active" : "" }`}
 >
 <AlertCircle className="w-3.5 h-3.5" />
 Expired{" "}
 <span className="tabular-nums opacity-70">({expiredVendorCount})</span>
 </button>
 </div>
 </div>

 <div className="p-4 sm:p-5 space-y-5">
 <div className="product-filter-panel">
 <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
 <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto flex-wrap">
 <div className="w-full sm:w-52">
 <SearchSelect
 label="Verification Status"
 value={filters.verification}
 onChange={(value) =>
 setFilters({ ...filters, verification: value })
 }
 options={[
 { value: "all", label: "All Verification" },
 { value: "verified", label: "Verified" },
 { value: "pending", label: "Pending" },
 ]}
 placeholder="Select verification status..."
 clearable={true}
 size="sm"
 />
 </div>

 <div className="w-full sm:w-48">
 <SearchSelect
 label="Featured Status"
 value={filters.featured}
 onChange={(value) =>
 setFilters({ ...filters, featured: value })
 }
 options={[
 { value: "all", label: "All Featured" },
 { value: "yes", label: "Featured" },
 { value: "no", label: "Not Featured" },
 ]}
 placeholder="Select featured status..."
 clearable={true}
 size="sm"
 />
 </div>

 <div className="w-full sm:w-48">
 <SearchSelect
 label="Subscription Status"
 value={filters.subscriptionStatus}
 onChange={(value) => {
 setFilters({ ...filters, subscriptionStatus: value });
 if (value === "expired") {
 setActiveSubscriptionTab("all");
 }
 }}
 options={[
 { value: "all", label: "All Subscriptions" },
 { value: "active", label: "Active" },
 { value: "expired", label: "Expired" },
 ]}
 placeholder="Select subscription status..."
 clearable={true}
 size="sm"
 />
 </div>

 <div className="w-full sm:w-48">
 <SearchSelect
 label="Online Status"
 value={filters.onlineStatus}
 onChange={(value) =>
 setFilters({ ...filters, onlineStatus: value })
 }
 options={[
 { value: "all", label: "All Status" },
 { value: "online", label: "Online" },
 { value: "offline", label: "Offline" },
 ]}
 placeholder="Select online status..."
 clearable={true}
 size="sm"
 />
 </div>
 </div>
 </div>
 </div>

 {error && (
 <div className="rounded-xl bg-red-50 border border-red-200 p-4">
 <div className="flex">
 <XCircle className="h-5 w-5 text-red-400" />
 <div className="ml-3">
 <h3 className="text-sm font-medium text-red-800">Error</h3>
 <div className="mt-2 text-sm text-red-700">{error}</div>
 </div>
 </div>
 </div>
 )}

 {loading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading vendors…</p>
 </div>
 ) : (
 <DataTable
 data={vendors}
 columns={columns}
 loading={false}
 searchable={true}
 filterable={true}
 sortable={true}
 selectable={true}
 onRowClick={handleRowClick}
 onRowSelect={setSelectedVendors}
 onSearch={handleSearch}
 actions={actions}
 searchPlaceholder="Search vendors by name, email, location, or online status..."
 emptyMessage="No vendors found"
 pageSize={20}
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>
 </div>
 );
};

export default AdminVendors;
